import { useEffect, useMemo, useRef, useState } from 'react';

export const useScreenAwake = () => {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  const refreshIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [isWakeLockSupported] = useState('wakeLock' in navigator);
  const [wakeLockEnabled, setWakeLockEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshWakeLock = async () => {
    const wakeLockActive = !!(wakeLockRef.current && !wakeLockRef.current.released);
    const wakeLockExpected = wakeLockEnabled && !document.hidden;
    if (wakeLockActive === wakeLockExpected) {
      return;
    }

    const tryRequest = async () => {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', refreshWakeLock);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to request wake lock');

        console.error('Failed to request wake lock:', err);
      }
    };

    const tryRelease = async () => {
      try {
        wakeLockRef.current?.removeEventListener('release', refreshWakeLock);
        await wakeLockRef.current?.release();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to release wake lock');

        console.error('Failed to release wake lock:', err);
      }
    };

    console.log('Wake Lock transitioning to:', wakeLockExpected);
    if (wakeLockExpected) {
      await tryRelease();
      await tryRequest();
    } else {
      await tryRelease();
    }
  };

  useEffect(() => {
    if (!isWakeLockSupported) {
      setError('Wake Lock API is not supported');

      return;
    }

    clearInterval(refreshIntervalRef.current);
    document.removeEventListener('visibilitychange', refreshWakeLock);

    if (wakeLockEnabled) {
      refreshIntervalRef.current = setInterval(refreshWakeLock, 1000);
      document.addEventListener('visibilitychange', refreshWakeLock);
    }

    refreshWakeLock();

    return () => {
      clearInterval(refreshIntervalRef.current);

      document.removeEventListener('visibilitychange', refreshWakeLock);
    };
  }, [wakeLockEnabled]);

  return useMemo(
    () => ({ isWakeLockSupported, setWakeLockEnabled, error }),
    [isWakeLockSupported, setWakeLockEnabled, error]
  );
};

export const useScreenAwakeForRecording = (isRecording: boolean) => {
  const wakeLock = useScreenAwake();

  useEffect(() => {
    if (!wakeLock.isWakeLockSupported) {
      return;
    }

    wakeLock.setWakeLockEnabled(isRecording);
  }, [isRecording, wakeLock]);

  return wakeLock;
};

export default useScreenAwake;
